import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ROLE_LABEL } from "../lib/constants";
import {
  LayoutDashboard, FileText, Package, ClipboardCheck, Receipt,
  Users, Database, ScrollText, LogOut, Search, Settings,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Input } from "./ui/input";

const NAV = {
  admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/returns", label: "Return Requests", icon: FileText },
    { to: "/inward", label: "Inward / Receipts", icon: Package },
    { to: "/inspection", label: "Quality Inspection", icon: ClipboardCheck },
    { to: "/credit-notes", label: "Credit Notes", icon: Receipt },
    { to: "/reports", label: "Reports", icon: ScrollText },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/masters", label: "Master Data", icon: Database },
    { to: "/audit-logs", label: "Audit Logs", icon: Settings },
  ],
  customer: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/returns", label: "My Returns", icon: FileText },
    { to: "/returns/new", label: "New Return Request", icon: FileText },
  ],
  inward: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/returns", label: "Return Requests", icon: FileText },
    { to: "/inward", label: "Material Receipts", icon: Package },
  ],
  quality: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/returns", label: "Return Requests", icon: FileText },
    { to: "/inspection", label: "Inspections", icon: ClipboardCheck },
  ],
  accounts: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/returns", label: "Return Requests", icon: FileText },
    { to: "/credit-notes", label: "Credit Notes", icon: Receipt },
  ],
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState("");
  const links = NAV[user?.role] || [];

  const submitSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/returns?q=${encodeURIComponent(search.trim())}`);
  };

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md brand-gradient flex items-center justify-center text-white font-bold heading">
              GR
            </div>
            <div>
              <div className="heading font-bold text-slate-900 leading-tight">Golden Rolls</div>
              <div className="text-xs text-slate-500">Return Management</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {links.map((l) => {
            const Icon = l.icon;
            const active = loc.pathname === l.to || (l.to !== "/dashboard" && loc.pathname.startsWith(l.to));
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`sidebar-link ${active ? "active" : ""}`}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <Icon className="w-4 h-4" />
                {l.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200 text-xs text-slate-500">
          v1.0 · {ROLE_LABEL[user?.role]}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 gap-4">
          <form onSubmit={submitSearch} className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="global-search-input"
              placeholder="Search requests, customers, LR No, item, CN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-slate-50 border-slate-200"
            />
          </form>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="profile-menu-button">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-orange-100 text-orange-700 text-xs font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <div className="text-sm font-medium text-slate-900 leading-tight">{user?.name}</div>
                    <div className="text-xs text-slate-500">{ROLE_LABEL[user?.role]}</div>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled className="text-xs">{user?.email}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}
